import React, { useEffect, useState, useRef } from 'react';
import { X, Calculator as CalcIcon, Info } from 'lucide-react';
interface CalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const CalculatorModal = ({
  isOpen,
  onClose
}: CalculatorModalProps) => {
  const [loanProduct, setLoanProduct] = useState('');
  const [loanAmount, setLoanAmount] = useState(100000);
  const [months, setMonths] = useState(24);
  const [interestRate, setInterestRate] = useState(3.5);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // Focus trap implementation
  useEffect(() => {
    if (!isOpen) return;
    const modal = modalRef.current;
    if (!modal) return;
    closeButtonRef.current?.focus();
    const focusableElements = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleTabKey);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const calculateLoan = () => {
    const principal = loanAmount;
    const monthlyRate = interestRate / 100;
    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    const totalAmount = monthlyPayment * months;
    const totalInterest = totalAmount - principal;
    return {
      monthlyPayment: Math.round(monthlyPayment),
      totalInterest: Math.round(totalInterest),
      totalAmount: Math.round(totalAmount)
    };
  };
  const results = calculateLoan();
  const loanProducts = [{
    value: 'logbook',
    label: 'Fast Cash Logbook Loan',
    rate: 3.5,
    method: 'Reducing Balance'
  }, {
    value: 'business',
    label: 'Secured Business Loan',
    rate: 2.5,
    method: 'Reducing Balance'
  }, {
    value: 'asset',
    label: 'Car & Asset Financing',
    rate: 1.0,
    method: 'Flat Rate (12% p.a.)'
  }, {
    value: 'trade',
    label: 'Trade Finance',
    rate: 2.0,
    method: 'Reducing Balance'
  }];
  const handleProductChange = (product: string) => {
    setLoanProduct(product);
    const selected = loanProducts.find(p => p.value === product);
    if (selected) {
      setInterestRate(selected.rate);
    }
  };
  const selectedProduct = loanProducts.find(p => p.value === loanProduct);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn" role="dialog" aria-modal="true" aria-labelledby="calculator-modal-title" data-id="element-339">
      <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" data-id="element-340">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10" data-id="element-341">
          <div className="flex items-center gap-3" data-id="element-342">
            <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center" data-id="element-343">
              <CalcIcon size={24} className="text-primary-600" aria-hidden="true" data-id="element-344" />
            </div>
            <h2 id="calculator-modal-title" className="text-2xl font-bold text-gray-900" data-id="element-345">
              Loan Calculator
            </h2>
          </div>
          <button ref={closeButtonRef} onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors duration-200" aria-label="Close loan calculator" data-id="element-346">
            <X size={24} data-id="element-347" />
          </button>
        </div>

        <div className="p-6" data-id="element-348">
          {/* Important Notice */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg" data-id="element-349">
            <div className="flex items-start gap-3" data-id="element-350">
              <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-351" />
              <div className="text-sm" data-id="element-352">
                <p className="font-semibold text-blue-900 mb-1" data-id="element-353">
                  Interest Rate Calculation Method
                </p>
                <p className="text-blue-800" data-id="element-354">
                  Rates shown are <strong data-id="element-355">per month (% p.m.)</strong> using the{' '}
                  <strong data-id="element-356">reducing balance method</strong> unless otherwise
                  specified. This means interest is calculated on the remaining
                  principal balance each month, not the original loan amount.
                </p>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8" data-id="element-357">
            {/* Calculator Inputs */}
            <div className="space-y-6" data-id="element-358">
              <div data-id="element-359">
                <label htmlFor="loan-product" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-360">
                  Select Loan Product *
                </label>
                <select id="loan-product" name="loan-product" value={loanProduct} onChange={e => handleProductChange(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" required aria-required="true" data-id="element-361">
                  <option value="" data-id="element-362">Choose a loan product...</option>
                  {loanProducts.map(product => <option key={product.value} value={product.value} data-id="element-363">
                      {product.label}
                    </option>)}
                </select>
                {selectedProduct && <p className="text-xs text-gray-600 mt-2" data-id="element-364">
                    <strong data-id="element-365">Calculation Method:</strong>{' '}
                    {selectedProduct.method}
                  </p>}
              </div>

              <div data-id="element-366">
                <label htmlFor="loan-amount" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-367">
                  Loan Amount (KSh): {loanAmount.toLocaleString()}
                </label>
                <input type="number" id="loan-amount" name="loan-amount" value={loanAmount} onChange={e => setLoanAmount(Number(e.target.value))} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-lg font-semibold" min="50000" max="10000000" step="10000" aria-label="Loan amount in Kenyan Shillings" data-id="element-368" />
                <input type="range" value={loanAmount} onChange={e => setLoanAmount(Number(e.target.value))} min="50000" max="10000000" step="10000" className="w-full mt-3" aria-label="Loan amount slider" data-id="element-369" />
              </div>

              <div data-id="element-370">
                <label htmlFor="loan-months" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-371">
                  Number of Months: {months}
                </label>
                <input type="number" id="loan-months" name="loan-months" value={months} onChange={e => setMonths(Number(e.target.value))} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-lg font-semibold" min="6" max="60" aria-label="Loan duration in months" data-id="element-372" />
                <input type="range" value={months} onChange={e => setMonths(Number(e.target.value))} min="6" max="60" className="w-full mt-3" aria-label="Loan duration slider" data-id="element-373" />
              </div>

              <div data-id="element-374">
                <label htmlFor="interest-rate" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-375">
                  Interest Rate: {interestRate}% per month
                </label>
                <input type="number" id="interest-rate" name="interest-rate" value={interestRate} onChange={e => setInterestRate(Number(e.target.value))} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-lg font-semibold" min="1" max="10" step="0.1" disabled={!!loanProduct} aria-label="Interest rate percentage per month" data-id="element-376" />
                <p className="text-xs text-gray-600 mt-2" data-id="element-377">
                  <strong data-id="element-378">Note:</strong> This is the monthly rate. Annual
                  equivalent: {(interestRate * 12).toFixed(1)}% p.a.
                </p>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-6" data-id="element-379">
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 text-white" data-id="element-380">
                <h3 className="text-xl font-bold mb-6" data-id="element-381">Your Results</h3>

                <div className="space-y-4" data-id="element-382">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4" data-id="element-383">
                    <p className="text-sm text-primary-100 mb-1" data-id="element-384">
                      Monthly Payment
                    </p>
                    <p className="text-3xl font-bold" aria-label={`Monthly payment: ${results.monthlyPayment.toLocaleString()} Kenyan Shillings`} data-id="element-385">
                      KSh {results.monthlyPayment.toLocaleString()}
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4" data-id="element-386">
                    <p className="text-sm text-primary-100 mb-1" data-id="element-387">
                      Total Interest
                    </p>
                    <p className="text-2xl font-bold" aria-label={`Total interest: ${results.totalInterest.toLocaleString()} Kenyan Shillings`} data-id="element-388">
                      KSh {results.totalInterest.toLocaleString()}
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4" data-id="element-389">
                    <p className="text-sm text-primary-100 mb-1" data-id="element-390">
                      Total Amount Payable
                    </p>
                    <p className="text-2xl font-bold" aria-label={`Total amount payable: ${results.totalAmount.toLocaleString()} Kenyan Shillings`} data-id="element-391">
                      KSh {results.totalAmount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4" data-id="element-392">
                <h4 className="font-bold text-gray-900 mb-3" data-id="element-393">Summary</h4>
                <div className="space-y-2 text-sm" data-id="element-394">
                  <div className="flex justify-between" data-id="element-395">
                    <span className="text-gray-600" data-id="element-396">Product:</span>
                    <span className="font-semibold text-gray-900" data-id="element-397">
                      {loanProduct ? loanProducts.find(p => p.value === loanProduct)?.label : 'Not selected'}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-398">
                    <span className="text-gray-600" data-id="element-399">Principal:</span>
                    <span className="font-semibold text-gray-900" data-id="element-400">
                      KSh {loanAmount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-401">
                    <span className="text-gray-600" data-id="element-402">Period:</span>
                    <span className="font-semibold text-gray-900" data-id="element-403">
                      {months} months
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-404">
                    <span className="text-gray-600" data-id="element-405">Rate:</span>
                    <span className="font-semibold text-gray-900" data-id="element-406">
                      {interestRate}% p.m.
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-407">
                    <span className="text-gray-600" data-id="element-408">Method:</span>
                    <span className="font-semibold text-gray-900" data-id="element-409">
                      {selectedProduct?.method || 'Reducing Balance'}
                    </span>
                  </div>
                </div>
              </div>

              <a href="/apply" className="block w-full text-center py-4 px-6 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-410">
                Apply for This Loan
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>;
};